import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  RadioGroup,
  Radio,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  ListItemText,
} from "@mui/material";
import { MoreHoriz, Info, Add } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MediaUploader from "./MediaUploader";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import VariantDialog from "./VariantDialog";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { setShowLoader } from "src/redux/slices/AuthSlice";
import { usePresignedUpload } from "src/hooks/usePresignedUpload";
import { MENU_ITEM_IMAGE, MENU_ITEM_VIDEO } from "src/utils/uploadCategories";

const useMenuItem = () => {
  return useMutation({
    mutationFn: async ({ businessId, data }) => {
      const res = await axiosInstance.post(
        `${ApiEndPoints.MENU_ITEM.add}/${businessId}`,
        data
      );
      return res?.data;
    },
  });
};

// Optimized mutation hooks for adding new items
const useAddMutation = (endpoint, queryKey) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (name) => {
      const response = await axiosInstance.post(endpoint, { name });
      return response.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries([queryKey]),
      onError: (error) => console.error(`Error adding ${queryKey}:`, error),
    }
  );
};

// Add edit mutation
const useEditMenuItem = () => {
  return useMutation({
    mutationFn: async ({ businessId, menuItemId, data }) => {
      const res = await axiosInstance.patch(
        `${ApiEndPoints.MENU_ITEM.add}/${menuItemId}`,
        data
      );
      return res?.data;
    },
  });
};

const AddMenuItemForm = ({
  IsMaxView = false,
  handleClose,
  editItem = null,
  isCatalog = false, // Add isCatalog prop
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [formErrors, setFormErrors] = useState({});
  const [formKey, setFormKey] = useState(Date.now());

  // Helper to clear a specific field error
  const clearFieldError = (field) => {
    setFormErrors((prev) => {
      if (!prev[field]) return prev;
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  // Helper function to set default currency for selected locations
  // Uses currency and currencySymbol directly from location object
  const setDefaultCurrencyForLocations = (selectedLocationValues) => {
    if (!selectedLocationValues?.length) return;

    const currentLocationValues = watch("locationValues") || {};
    const updatedLocationValues = { ...currentLocationValues };

    // Get previous locations to find newly selected ones
    const previousLocationValues = watch("locations") || [];
    const newlySelected = selectedLocationValues.filter(
      (loc) => !previousLocationValues.includes(loc)
    );
    
    console.log("setDefaultCurrencyForLocations called", {
      selectedLocationValues,
      previousLocationValues,
      newlySelected,
      businessLocationAvailable: !!businessLocation?.locations,
      locationsCount: businessLocation?.locations?.length
    });

    // Update currency for all selected locations using currency from location object
    if (businessLocation?.locations) {
      selectedLocationValues.forEach((locValue) => {
        // Find location object by value
        const locObj = locationsListOptions.find((loc) => loc.value === locValue);
        
        // Find full location data from businessLocation
        let fullLocation = null;
        
        if (locObj) {
          fullLocation = businessLocation.locations.find(
            (loc) => loc._id === locObj.id
          );
        }
        
        // Fallback: try matching directly by businessLocationName
        if (!fullLocation) {
          fullLocation = businessLocation.locations.find(
            (loc) => loc.businessLocationName === locValue
          );
        }
        
        if (!fullLocation) {
          console.warn(`Full location not found for value: ${locValue}`);
          // Set default USD if location not found
          if (!updatedLocationValues[locValue]) {
            updatedLocationValues[locValue] = {};
          }
          if (!updatedLocationValues[locValue].currency) {
            updatedLocationValues[locValue].currency = "USD";
          }
          return;
        }

        // Use currency from location object (default to USD if not available)
        const locationCurrency = fullLocation.currency || "USD";
        
        // Initialize locationValues entry if it doesn't exist with all required fields
        if (!updatedLocationValues[locValue]) {
          updatedLocationValues[locValue] = {
            currency: locationCurrency,
            price: 0,
            sku: "",
            stockOnHand: "",
            stockCommitted: "",
            awaitingNewStockDelivery: "",
            awaitingNewStockCount: "",
          };
        } else {
          // Ensure currency is set even if entry exists
          if (!updatedLocationValues[locValue].currency) {
            updatedLocationValues[locValue].currency = locationCurrency;
          }
          // Ensure all fields exist
          updatedLocationValues[locValue] = {
            currency: updatedLocationValues[locValue].currency || locationCurrency,
            price: updatedLocationValues[locValue].price !== undefined ? updatedLocationValues[locValue].price : 0,
            sku: updatedLocationValues[locValue].sku || "",
            stockOnHand: updatedLocationValues[locValue].stockOnHand !== undefined ? updatedLocationValues[locValue].stockOnHand : "",
            stockCommitted: updatedLocationValues[locValue].stockCommitted !== undefined ? updatedLocationValues[locValue].stockCommitted : "",
            awaitingNewStockDelivery: updatedLocationValues[locValue].awaitingNewStockDelivery || "",
            awaitingNewStockCount: updatedLocationValues[locValue].awaitingNewStockCount !== undefined ? updatedLocationValues[locValue].awaitingNewStockCount : "",
          };
        }
        
        console.log(`Currency for ${locValue}:`, { 
          locationCurrency, 
          currencySymbol: fullLocation.currencySymbol 
        });
      });

      // Update form with all currency changes at once
      setValue("locationValues", updatedLocationValues);
    } else {
      // If businessLocation is not available, set default USD
      selectedLocationValues.forEach((locValue) => {
        if (!updatedLocationValues[locValue]) {
          updatedLocationValues[locValue] = {};
        }
        if (!updatedLocationValues[locValue].currency) {
          updatedLocationValues[locValue].currency = "USD";
        }
      });
      setValue("locationValues", updatedLocationValues);
    }
  };

  // Transform API data for editing
  const transformEditData = (item) => {
    if (!item) return null;

    // Transform dietTypes from array of objects to object with IDs as keys
    const dietTypesObj = {};
    if (item.dietTypes && Array.isArray(item.dietTypes)) {
      item.dietTypes.forEach((diet) => {
        dietTypesObj[diet._id] = true;
      });
    }

    // Transform ingredients with substitutes - extract just the names
    const ingredients = item.ingredients?.map((ing) => ing.name) || [];
    const substitutions = item.ingredients?.map((ing) => ({
      ingredient: ing.name,
      subs: ing.substitutes || [],
    })) || [];

    // Transform locationStock to locationValues (now includes price and currency)
    const locationValues = {};
    const locations = [];
    if (item.locationStock && Array.isArray(item.locationStock)) {
      item.locationStock.forEach((stock) => {
        // Handle both locationId as string and locationId as object
        let locationId = typeof stock.locationId === 'string' 
          ? stock.locationId 
          : stock.locationId?._id;
        
        // Extract businessLocationName if locationId is an object
        let locationName = null;
        if (typeof stock.locationId === 'object' && stock.locationId?.businessLocationName) {
          locationName = stock.locationId.businessLocationName;
        }
        
        // Use locationName if available, otherwise use locationId
        // The form expects location values (businessLocationName) as keys
        const locationKey = locationName || locationId;
        
        if (locationKey) {
          locations.push(locationKey);
          locationValues[locationKey] = {
            currency: stock.currency || "USD",
            price: stock.price !== undefined && stock.price !== null ? stock.price : 0,
            sku: stock.sku || "",
            stockOnHand: stock.stockOnHand !== undefined && stock.stockOnHand !== null ? stock.stockOnHand : "",
            stockCommitted: stock.stockCommitted !== undefined && stock.stockCommitted !== null ? stock.stockCommitted : "",
            awaitingNewStockDelivery: stock.awaitingNewStockDelivery || "",
            awaitingNewStockCount: stock.awaitingNewStockCount !== undefined && stock.awaitingNewStockCount !== null ? stock.awaitingNewStockCount : "",
          };
        }
      });
    }

    // Transform variants data properly - handle both catalog and menu items
    const transformedVariants = item.variants?.map(variant => {
      
              // For catalog items, handle the different structure
        if (isCatalog) {
          // Extract variant number from variant.code if available (format: "145-01")
          const variantCode = variant.code || "";
          const variantNumber = variant.variantNumber || (variantCode.includes("-") 
            ? variantCode.split("-").pop() 
            : "");
          
          // Location stock (may be empty for catalog items) - now includes price and currency
          const locationStock = variant.perLocationStock?.map(locStock => ({
            locationId: typeof locStock.locationId === 'string' 
              ? locStock.locationId 
              : locStock.locationId?._id || locStock.locationId?._id,
            currency: locStock.currency || "USD",
            price: Number(locStock.price || 0),
            sku: locStock.sku || "",
            stockOnHand: Number(locStock.stockOnHand || locStock.qty || 0),
            stockCommitted: Number(locStock.stockCommitted || 0),
            awaitingNewStockDelivery: locStock.awaitingNewStockDelivery || "",
            awaitingNewStockCount: Number(locStock.awaitingNewStockCount || 0),
          })) || [];
          
          // Calculate stockCount from location stock
          const calculatedStockCount = locationStock.reduce(
            (total, loc) => total + (Number(loc.stockOnHand) || 0),
            0
          );
          
          return {
            variant: variant.name,
            description: variant.description || "",
            variantNumber: variantNumber,
            price: variant.price || 0,
            currency: variant.currency || "USD",
            
            // For catalog items, calculate stockOnHand from location stock
            stockOnHand: calculatedStockCount || variant.stockCount || 0,
            stockCount: calculatedStockCount || variant.stockCount || 0,
          
          // Nutritional fields (may be empty for catalog items)
          weight: variant.nutrition?.weight || 0,
          kcal: variant.nutrition?.kcal || 0,
          protein: variant.nutrition?.protein || 0,
          carbohydrates: variant.nutrition?.carbohydrates || 0,
          fats: variant.nutrition?.fats || 0,
          fibre: variant.nutrition?.fibre || 0,
          
          // Stock flags
          showStockToCustomer: variant.showStockToCustomer || false,
          allowSaleIfOutOfStock: variant.allowSaleIfOutOfStock || false,
          
          // Location stock - stock information is now location-based
          locationStock: locationStock,
        };
      } else {
        // For menu items, use the existing logic
        // Extract variant number from variant.code if available (format: "145-01")
        const variantCode = variant.code || "";
        const variantNumber = variant.variantNumber || (variantCode.includes("-") 
          ? variantCode.split("-").pop() 
          : "");
        
        return {
          variant: variant.name,
          description: variant.description || "",
          variantNumber: variantNumber,
          price: variant.price || 0,
          currency: variant.currency || "USD",
          
          // For menu items, use stockOnHand from perLocationStock
          stockOnHand: variant.perLocationStock?.[0]?.stockOnHand || 0,
          
          // Nutritional information (for menu items)
          weight: variant.nutrition?.weight || 0,
          kcal: variant.nutrition?.kcal || 0,
          protein: variant.nutrition?.protein || 0,
          carbohydrates: variant.nutrition?.carbohydrates || 0,
          fats: variant.nutrition?.fats || 0,
          fibre: variant.nutrition?.fibre || 0,
          
          // SKU and barcode
          sku: variant.sku || "",
          barcode: variant.sku || "",
          qrCodeImage: variant.qrCodeImage || "",
          barcodeImage: variant.barcodeImage || "",
          
          // Stock information for menu items
          stockCount: variant.stockCount || 0,
          stockCommitted: variant.stockCommitted || 0,
          awaitingNewDelivery: variant.awaitingNewDelivery,
          awaitingNewStockCount: variant.awaitingNewStockCount,
          showStockToCustomer: variant.showStockToCustomer || false,
          allowSaleIfOutOfStock: variant.allowSaleIfOutOfStock || false,
          
          // Transform perLocationStock to locationStock format for the form - now includes price and currency
          locationStock: variant.perLocationStock?.map(locStock => ({
            locationId: typeof locStock.locationId === 'string' 
              ? locStock.locationId 
              : locStock.locationId?._id || locStock.locationId?._id,
            currency: locStock.currency || "USD",
            price: Number(locStock.price || 0),
            sku: locStock.sku || "",
            stockOnHand: Number(locStock.stockOnHand || 0),
            stockCommitted: Number(locStock.stockCommitted || 0),
            awaitingNewStockDelivery: locStock.awaitingNewStockDelivery || "",
            awaitingNewStockCount: Number(locStock.awaitingNewStockCount || 0),
          })) || [],
        };
      }
    }) || [];

    // Ensure sides is always an array of strings/IDs
    const sides = Array.isArray(item.sides) 
      ? item.sides.map(side => typeof side === 'string' ? side : side._id || side.id || '')
      : [];

    const transformedData = {
      menuItemCode: item.code || "",
      menuItemName: item.name || "",
      foodType: item.foodType?._id || item.foodType || "",
      itemDescription: item.description || "",
      prepTime: item.prepTime || 15,
      waitTime: item.waitTime || 15,
      dietTypes: dietTypesObj,
      ingredients: ingredients,
      allowFreeSubstitutions: item.allowFreeSubstitution || false,
      substitutions: substitutions,
      allowAdditionOfSides: item.allowSideAddition || false,
      sides: sides.filter(Boolean), // Remove any empty strings
      allowIngredientRemovals: item.allowIngredientRemoval || false,
      removableIngredients: item.removableIngredients || [],
      allergens: item.allergens || [],
      hasVariant: item.variantEnabled || false,
      variants: item.variants || [],
      mediaFiles:
        item.imageVideo?.map((img) => ({
          file: img,
          type: "image",
          preview: img,
        })) || [],
      variantRows: transformedVariants,
      currentVariantValue: null,
      dialogOpen: false,
      locations: locations,
      locationValues: locationValues,
      showStockToCustomer: item.showStockToCustomer || false,
      allowSaleIfOutOfStock: item.allowSaleIfOutOfStock || false,
    };
    
    return transformedData;
  };

  // Set default values based on editItem
  const getDefaultValues = () => {
    if (editItem) {
      return transformEditData(editItem);
    }

    return {
      sku: "",
      barcode: "",
      gtinCode: "",
      location: "",
      stockOnHand: 0,
      stockCommitted: 0,
      awaitingNewStockDelivery: "",
      awaitingNewStockCount: 0,
      showStockToCustomer: false,
      allowSaleIfOutOfStock: false,
      menuItemCode: "",
      menuItemName: "",
      foodType: "",
      itemDescription: "",
      prepTime: 15,
      waitTime: 15,
      dietTypes: {},
      ingredients: [],
      allowFreeSubstitutions: false,
      substitutions: [],
      allowAdditionOfSides: false,
      sides: [],        // Changed back to 'sides' to match backend expectation
      allowIngredientRemovals: false,
      removableIngredients: [],
      allergens: [],
      hasVariant: false,
      variants: [],
      mediaFiles: [],
      variantRows: [],
      currentVariantValue: null,
      dialogOpen: false,
      locations: [],
      locationValues: {},
    };
  };

  // Form setup with optimized default values
  const { control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: getDefaultValues(),
  });

  // Reset form when editItem changes
  React.useEffect(() => {
    if (editItem) {
      const transformedData = transformEditData(editItem);
      // Ensure locationValues are properly structured
      if (transformedData.locations && transformedData.locations.length > 0) {
        // Make sure all location values have complete structure
        const completeLocationValues = {};
        transformedData.locations.forEach(loc => {
          if (transformedData.locationValues[loc]) {
            const locVal = transformedData.locationValues[loc];
            completeLocationValues[loc] = {
              currency: locVal.currency || "USD",
              price: locVal.price !== undefined && locVal.price !== null ? locVal.price : 0,
              sku: locVal.sku || "",
              stockOnHand: locVal.stockOnHand !== undefined && locVal.stockOnHand !== null ? locVal.stockOnHand : "",
              stockCommitted: locVal.stockCommitted !== undefined && locVal.stockCommitted !== null ? locVal.stockCommitted : "",
              awaitingNewStockDelivery: locVal.awaitingNewStockDelivery || "",
              awaitingNewStockCount: locVal.awaitingNewStockCount !== undefined && locVal.awaitingNewStockCount !== null ? locVal.awaitingNewStockCount : "",
            };
          }
        });
        transformedData.locationValues = completeLocationValues;
      }
      reset(transformedData);
      
      // Log the form state after reset
      setTimeout(() => {
        console.log('Form state after reset:', {
          variantRows: watch('variantRows'),
          hasVariant: watch('hasVariant'),
          ingredients: watch('ingredients'),
          dietTypes: watch('dietTypes'),
          locations: watch('locations'),
          locationValues: watch('locationValues'),
        });
        
        // Additional logging for catalog items
        if (isCatalog) {
          console.log('Catalog item variant details:', watch('variantRows')?.map(v => ({
            variant: v.variant,
            stockCount: v.stockCount,
            stockOnHand: v.stockOnHand,
            price: v.price,
            description: v.description
          })));
        }
      }, 100);
    } else {
      // Reset to default values when editItem is null (create mode)
      const defaultValues = getDefaultValues();
      reset(defaultValues, { keepDefaultValues: false });
      // Explicitly clear foodType to ensure Autocomplete resets
      setValue("foodType", "");
      // Also clear any form errors
      setFormErrors({});
    }
  }, [editItem, reset, isCatalog, setValue]);

  // Presigned upload (replaces direct multipart for images and videos)
  const { mutateAsync: uploadFileWithPresignedUrl } = usePresignedUpload();
  const { mutate: submitMenuItem, isLoading: isSubmitting } = useMenuItem();
  const { mutate: editMenuItem, isLoading: isEditing } = useEditMenuItem();

  // Optimized add mutations
  const addIngredientMutation = useAddMutation(
    ApiEndPoints.COMMON.ingredient,
    "ingredient"
  );
  const addAllergenMutation = useAddMutation(
    ApiEndPoints.COMMON.allergen,
    "allergen"
  );
  const addFoodTypeMutation = useAddMutation(
    ApiEndPoints.COMMON.foodType,
    "foodType"
  );
  const addSideMutation = useAddMutation(ApiEndPoints.COMMON.sides, "sides");

  // Watch form values
  const [
    selectedIngredients,
    allowFreeSubstitutions,
    allowAdditionOfSides,
    allowIngredientRemovals,
    hasVariant,
    mediaFiles,
    variantRows,
    dialogOpen,
    currentVariantValue,
    locations,
    locationValues,
  ] = watch([
    "ingredients",
    "allowFreeSubstitutions",
    "allowAdditionOfSides",
    "allowIngredientRemovals",
    "hasVariant",
    "mediaFiles",
    "variantRows",
    "dialogOpen",
    "currentVariantValue",
    "locations",
    "locationValues",
  ]);

  // Data fetching queries
  const { data: foodType } = useQuery(
    ["foodType"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.foodType, {
        signal,
      });
      return res.data.data;
    },
    { refetchOnWindowFocus: false, cacheTime: 30 * 60 * 1000 }
  );

  const { data: dietType } = useQuery(
    ["dietType"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.dietType, {
        signal,
      });
      return res.data.data;
    },
    { refetchOnWindowFocus: false, cacheTime: 30 * 60 * 1000 }
  );

  const { data: ingredients } = useQuery(
    ["ingredient"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.ingredient, {
        signal,
      });
      return res.data.data;
    },
    { refetchOnWindowFocus: false, cacheTime: 30 * 60 * 1000 }
  );

  const { data: allergens } = useQuery(
    ["allergen"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.allergen, {
        signal,
      });
      return res.data.data;
    },
    { refetchOnWindowFocus: false, cacheTime: 30 * 60 * 1000 }
  );

  const { data: existingMenuItems } = useQuery(
    ["menuItems", selectedBusinessId],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.MENU_ITEM.getAll}/${selectedBusinessId}?isCatalog=false`,
        { signal }
      );
      return res.data.data;
    },
    { refetchOnWindowFocus: false, cacheTime: 30 * 60 * 1000 }
  );

  // Fix: Keep the original businessLocation query for locations
  const { data: businessLocation } = useQuery(
    ["businessLocation"],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.BUSINESS_LOCATION.getAll}/${selectedBusinessId}`,
        { signal }
      );
      return res.data.data;
    },
    { refetchOnWindowFocus: false, cacheTime: 30 * 60 * 1000 }
  );

  // Currency query with optional search parameter
  const { data: currencyData } = useQuery(
    ["currency"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.currency, {
        signal,
      });
      return res.data.data;
    },
    { refetchOnWindowFocus: false, cacheTime: 30 * 60 * 1000 }
  );

  // Memoized options to prevent unnecessary re-renders - updated to lowercase
  const options = useMemo(
    () => {
      if (isCatalog) {
        // Catalog items use size-based variants
        return [
          { value: "S", label: "size s" },
          { value: "M", label: "size m" },
          { value: "L", label: "size l" },
          { value: "XL", label: "size xl" },
          { value: "XXL", label: "size xxl" },
        ];
      } else {
        // Menu items use portion-based variants
        return [
          { value: "full", label: "full" },
          { value: "half-full", label: "half-full" },
          { value: "large", label: "large" },
          { value: "medium", label: "medium" },
          { value: "small", label: "small" },
        ];
      }
    },
    [isCatalog]
  );

  const ingredientOptions = useMemo(
    () =>
      ingredients?.ingredients?.map((ingredient) => ({
        label: ingredient.name,
        value: ingredient.name,
        id: ingredient._id,
      })) || [],
    [ingredients]
  );

  const allergenOptions = useMemo(
    () =>
      allergens?.allergens?.map((allergen) => ({
        label: allergen.name,
        value: allergen.name,
        id: allergen._id,
      })) || [],
    [allergens]
  );

  const foodTypeOptions = useMemo(
    () =>
      foodType?.foodTypes?.map((type) => ({
        label: type.name,
        value: type.name,
        id: type._id,
      })) || [],
    [foodType]
  );

  // Replace sideOptions with menuItemOptions
  const menuItemOptions = useMemo(
    () => {
      if (!existingMenuItems?.items) return [];
      
      return existingMenuItems.items
        .filter((menuItem) => {
          // If we're editing an item, exclude it from the sides list
          if (editItem && editItem._id) {
            return menuItem._id !== editItem._id;
          }
          // If creating new item, show all menu items
          return true;
        })
        .map((menuItem) => ({
          label: menuItem.name,        // "French Fries", "Burger", etc.
          value: menuItem._id,         // Menu item ID for backend
          id: menuItem._id,            // MongoDB ID
          code: menuItem.code,         // Menu item code
          price: menuItem.price,       // Price for display
          currency: menuItem.currency  // Currency for display
        }));
    },
    [existingMenuItems, editItem]  // Add editItem to dependencies
  );

  // Fix: Keep the original locationsListOptions using businessLocation
  const locationsListOptions = useMemo(
    () =>
      businessLocation?.locations?.map((location) => ({
        label: location.businessLocationName,
        value: location.businessLocationName,
        id: location._id,
      })) || [],
    [businessLocation]  // Changed back to businessLocation
  );

  

  // Optimized handlers using form setValue directly
  const handleSubstitutionChange = (ingredient, subIndex, value) => {
    const currentSubstitutions = watch("substitutions") || [];
    const existingSubIndex = currentSubstitutions.findIndex(
      (sub) => sub.ingredient === ingredient
    );

    if (existingSubIndex >= 0) {
      const updatedSubs = [...currentSubstitutions[existingSubIndex].subs];
      updatedSubs[subIndex] = value;
      const updatedSubstitutions = [...currentSubstitutions];
      updatedSubstitutions[existingSubIndex] = {
        ...updatedSubstitutions[existingSubIndex],
        subs: updatedSubs,
      };
      setValue("substitutions", updatedSubstitutions);
    } else {
      const newSubs = ["", "", ""];
      newSubs[subIndex] = value;
      setValue("substitutions", [
        ...currentSubstitutions,
        { ingredient, subs: newSubs },
      ]);
    }
  };

  const getSubstitutionValue = (ingredient, subIndex) => {
    const substitutions = watch("substitutions") || [];
    const sub = substitutions.find((s) => s.ingredient === ingredient);
    return sub?.subs?.[subIndex] || "";
  };

  // Upload a single media file via presigned URL, returns the fileKey
  const uploadSingleFile = async (file) => {
    const category = file.type?.startsWith("video/") ? MENU_ITEM_VIDEO : MENU_ITEM_IMAGE;
    const result = await uploadFileWithPresignedUrl({ file, category });
    return result?.fileUrl;
  };

  const uploadMediaFiles = async (mediaFiles) => {
    const uploadResults = [];
    for (const mediaFile of mediaFiles) {
      if (mediaFile.file instanceof File) {
        try {
          const fileKey = await uploadSingleFile(mediaFile.file);
          if (fileKey) uploadResults.push(fileKey);
        } catch (error) {
          console.error("Failed to upload file:", mediaFile.file.name, error);
          toast.error(`Failed to upload ${mediaFile.file.name}`);
        }
      }
    }
    return uploadResults;
  };

  // Optimized variant handlers
  const handleVariantSelectionChange = (selectedValues) => {
    const currentRows = watch("variantRows") || [];
    const updatedRows = selectedValues.map((variant) => {
      const found = currentRows.find((row) => row.variant === variant);
      if (found) {
        // Preserve existing data
        return found;
      } else {
        // Create new row with default values based on isCatalog
        if (isCatalog) {
          // For catalog items, initialize with catalog-specific defaults
          return { 
            variant, 
            stockOnHand: 0, // Will be synced with stockCount
            description: "",
            variantNumber: "",
            // Catalog-specific fields
            gtin: "",
            sku: "",
            barcode: "",
            stockCount: 0, // Main stock field for catalog items
            stockCommitted: 0,
            awaitingNewDelivery: "",
            // Nutritional fields (may be empty for catalog items)
            weight: 0,
            kcal: 0,
            protein: 0,
            carbohydrates: 0,
            fats: 0,
            fibre: 0,
            locationStock: [],
          };
        } else {
          // For menu items, initialize with menu-specific defaults
          return { 
            variant, 
            stockOnHand: 0, 
            description: "",
            variantNumber: "",
            // Initialize nutritional fields for menu items
            weight: 0,
            kcal: 0,
            protein: 0,
            carbohydrates: 0,
            fats: 0,
            fibre: 0,
            // Menu-specific fields
            gtin: "",
            sku: "",
            barcode: "",
            stockCount: 0,
            stockCommitted: 0,
            awaitingNewDelivery: "",
            locationStock: [],
          };
        }
      }
    });
    setValue("variantRows", updatedRows);
  };

  const handleOpenDetails = (variantValue) => {

    
    const currentRow = (variantRows || []).find(row => row.variant === variantValue);
    
    const transformedData = transformVariantDataForDialog(currentRow);
    
    setValue("currentVariantValue", variantValue);
    setValue("dialogOpen", true);
  };

  // Helper function to transform variant data for the dialog
  const transformVariantDataForDialog = (variantRow) => {
    if (!variantRow) return {};
        
    // Transform location data for menu items - FIXED to handle removals properly
    let locations = [];
    let locationValues = {};
    
    if (variantRow.locationStock && Array.isArray(variantRow.locationStock)) {
      // Only include locations that actually exist in locationStock
      locations = variantRow.locationStock.map(loc => {
        // Find location value by ID
        const locObj = locationsListOptions.find(l => l.id === loc.locationId);
        return locObj?.value || loc.locationId;
      }).filter(Boolean); // Remove any undefined values
      
      locationValues = variantRow.locationStock.reduce((acc, loc) => {
        const locObj = locationsListOptions.find(l => l.id === loc.locationId);
        const locKey = locObj?.value || loc.locationId;
        if (locKey) {
          acc[locKey] = {
            currency: loc.currency || "USD",
            price: loc.price || 0,
            sku: loc.sku || "",
            stockOnHand: loc.stockOnHand || "", // Use stockOnHand consistently
            stockCommitted: loc.stockCommitted || "",
            awaitingNewStockDelivery: loc.awaitingNewStockDelivery || "",
            awaitingNewStockCount: loc.awaitingNewStockCount || "",
          };
        }
        return acc;
      }, {});
    }
    
    // Handle catalog vs menu items differently
    if (isCatalog) {
      // For catalog items, stock information is now location-based only
      const transformedData = {
        variant: variantRow.variant || "",
        variantDescription: variantRow.description || "",
        variantNumber: variantRow.variantNumber || "",
        stockOnHand: variantRow.stockOnHand || 0, // Calculated from location stock
        // Map location data (may be empty for catalog items) - now includes price/currency
        locations: locations,
        locationValues: locationValues,
      };

      return transformedData;
    } else {
      // For menu items, use the existing logic
      const transformedData = {
        variant: variantRow.variant || "",
        variantDescription: variantRow.description || "",
        variantNumber: variantRow.variantNumber || "",
        stockOnHand: variantRow.stockOnHand || 0, // Use stockOnHand instead of qty
        // Map nutritional fields for menu items
        totalWeight: variantRow.weight || "",
        kcal: variantRow.kcal || "",
        protein: variantRow.protein || "",
        carbs: variantRow.carbohydrates || "",
        fats: variantRow.fats || "",
        fibre: variantRow.fibre || "",
        // Map catalog-specific fields
        gtin: variantRow.gtin || "",
        sku: variantRow.sku || "",
        barcode: variantRow.barcode || "",
        stockCount: variantRow.stockCount || variantRow.stockOnHand || "", // Use stockOnHand as fallback
        stockCommitted: variantRow.stockCommitted || "",
        awaitingNewDelivery: variantRow.awaitingNewDelivery || "",
        // Map location data for menu items - now includes price/currency
        locations: locations,
        locationValues: locationValues,
      };
      
      return transformedData;
    }
  };

  // Update the handleSaveVariantDetails function to properly handle location changes
  const handleSaveVariantDetails = (data) => {

    const rows = watch("variantRows") || [];
    const currentVariantValue = watch("currentVariantValue");
        
    const updatedRows = rows.map((row) =>
      row.variant === currentVariantValue
        ? {
            ...row,
            variant: row.variant, // keep original
            description: data.variantDescription || "",
            variantNumber: data.variantNumber || "",

            // Handle stockOnHand differently for catalog vs menu items
            stockOnHand: isCatalog 
              ? (data.locationStock || []).reduce(
                  (total, loc) => total + (Number(loc.stockOnHand) || 0),
                  0
                )
              : (data.locationStock || []).reduce(
                  (total, loc) => total + (Number(loc.stockOnHand) || 0),
                  0
                ),
            
            // Nutrition (only for menu items)
            ...(!isCatalog && {
              weight: Number(data.totalWeight) || 0,
              kcal: Number(data.kcal) || 0,
              protein: Number(data.protein) || 0,
              carbohydrates: Number(data.carbs) || 0,
              fats: Number(data.fats) || 0,
              fibre: Number(data.fibre) || 0,
            }),

            // Catalog-specific fields - stock is now location-based
            ...(isCatalog && {
              stockCount: (data.locationStock || []).reduce(
                (total, loc) => total + (Number(loc.stockOnHand) || 0),
                0
              ),
            }),

            // CRITICAL FIX: Properly map location stock data and handle removals
            locationStock: (data.locationStock || []).map((loc) => ({
              locationId: loc.locationId,
              currency: loc.currency || "USD",
              price: Number(loc.price) || 0,
              sku: loc.sku || "",
              stockOnHand: Number(loc.stockOnHand) || 0,
              stockCommitted: Number(loc.stockCommitted) || 0,
              awaitingNewStockDelivery: loc.awaitingNewStockDelivery || "",
              awaitingNewStockCount: Number(loc.awaitingNewStockCount) || 0,
            })),
          }
        : row
    )
    
    // Force a re-render by using trigger
    setValue("variantRows", updatedRows, { shouldValidate: true, shouldDirty: true });
    
    setValue("dialogOpen", false);
    setValue("currentVariantValue", null);
  };

  // Optimized add handlers
  const handleAddNew = async (
    value,
    mutationFn,
    fieldName,
    isMultiple = false
  ) => {
    if (value?.trim()) {
      try {
        await mutationFn.mutateAsync(value.trim());
        if (isMultiple) {
          const currentValues = watch(fieldName) || [];
          setValue(fieldName, [...currentValues, value.trim()]);
        } else {
          setValue(fieldName, value.trim());
        }
      } catch (error) {
        console.error(`Error adding ${fieldName}:`, error);
      }
    }
  };

  // Add this helper function similar to VariantDialog
  const transformLocationValuesWithId = (
    locationValues = {},
    locationsListOptions = []
  ) => {
    return Object.entries(locationValues).map(([locationKey, values]) => {
      const locationObj = locationsListOptions.find(
        (loc) => loc.value === locationKey
      );
      return {
        locationId: locationObj ? locationObj.id : locationKey,
        currency: values.currency || "USD",
        price: Number(values.price) || 0,
        sku: values.sku || "",
        stockOnHand: Number(values.stockOnHand) || 0,
        stockCommitted: Number(values.stockCommitted) || 0,
        awaitingNewStockDelivery: values.awaitingNewStockDelivery || "",
        awaitingNewStockCount: Number(values.awaitingNewStockCount) || 0,
      };
    });
  };

  // Add location fields definition
  const locationFields = [
    { name: "currency", label: "currency", type: "select" },
    { name: "price", label: "price", type: "number" },
    { name: "sku", label: "sku", type: "text" },
    { name: "stockOnHand", label: "stock on hand", type: "number" },
    { name: "stockCommitted", label: "stock committed", type: "number" },
    {
      name: "awaitingNewStockDelivery",
      label: "awaiting new stock delivery",
      type: "date",
    },
    {
      name: "awaitingNewStockCount",
      label: "awaiting new stock count",
      type: "number",
    },
  ];

  // Add function to render location fields
  const renderLocationFields = () =>
    (locations || []).map((locValue) => {
      // Find location object by value first, then by id as fallback
      const locObj = locationsListOptions.find((loc) => loc.value === locValue) || 
                     locationsListOptions.find((loc) => loc.id === locValue);
      const locLabel = locObj?.label || locValue;
      const locationCurrency = locationValues?.[locValue]?.currency || "USD";
      return (
        <Box
          key={locValue}
          sx={{ mb: 2, border: "1px solid #eee", borderRadius: 2, p: 1 }}
        >
          <Typography sx={{ fontWeight: "bold", mb: 1 }}>{locLabel}</Typography>
          <Grid container spacing={1} alignItems="center">
            {locationFields.map((field) => (
              <React.Fragment key={field.name}>
                <Grid item xs={8}>
                  <Typography>{field.label}</Typography>
                </Grid>
                <Grid item xs={4}>
                  {field.name === "currency" ? (
                    <Controller
                      name={`locationValues.${locValue}.${field.name}`}
                      control={control}
                      render={({ field: innerField }) => {
                        const currencyValue = innerField.value || locationCurrency || "USD";
                        return (
                          <Autocomplete
                            options={currencies}
                            getOptionLabel={(option) => option.label || ""}
                            value={
                              currencies.find(
                                (currency) => currency.code === currencyValue
                              ) || currencies.find((c) => c.code === "USD") || currencies[0]
                            }
                            onChange={(event, newValue) => {
                              innerField.onChange(newValue ? newValue.code : "USD");
                              clearFieldError(
                                `locationValues.${locValue}.${field.name}`
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="search currency..."
                                disabled
                              />
                            )}
                            disabled
                            fullWidth
                          />
                        );
                      }}
                    />
                  ) : field.name === "price" ? (
                    <TextField
                      name={`locationValues.${locValue}.${field.name}`}
                      type="number"
                      fullWidth
                      size="small"
                      value={locationValues?.[locValue]?.[field.name] ?? ""}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {currencies.find((c) => c.code === locationCurrency)?.symbol || "$"}
                          </InputAdornment>
                        ),
                      }}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const currentLocationValues = watch("locationValues") || {};
                        const updatedLocationValues = {
                          ...currentLocationValues,
                          [locValue]: {
                            ...(currentLocationValues[locValue] || {}),
                            [field.name]: newValue,
                          },
                        };
                        setValue("locationValues", updatedLocationValues, { shouldValidate: false, shouldDirty: true });
                        clearFieldError(
                          `locationValues.${locValue}.${field.name}`
                        );
                      }}
                    />
                  ) : (
                    (() => {
                      // Handle date field formatting
                      let displayValue = locationValues?.[locValue]?.[field.name] ?? "";
                      if (field.type === "date" && displayValue) {
                        // Convert date to YYYY-MM-DD format for input
                        try {
                          const date = new Date(displayValue);
                          if (!isNaN(date.getTime())) {
                            displayValue = date.toISOString().split('T')[0];
                          } else {
                            displayValue = "";
                          }
                        } catch (e) {
                          displayValue = "";
                        }
                      }
                      
                      return (
                        <TextField
                          name={`locationValues.${locValue}.${field.name}`}
                          type={field.type}
                          fullWidth
                          size="small"
                          value={displayValue}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            const currentLocationValues = watch("locationValues") || {};
                            const updatedLocationValues = {
                              ...currentLocationValues,
                              [locValue]: {
                                ...(currentLocationValues[locValue] || {}),
                                [field.name]: newValue,
                              },
                            };
                            setValue("locationValues", updatedLocationValues, { shouldValidate: false, shouldDirty: true });
                            clearFieldError(
                              `locationValues.${locValue}.${field.name}`
                            );
                          }}
                        />
                      );
                    })()
                  )}
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Box>
      );
    });

  // Main submit handler
  const onSubmit = async (data) => {
    // --- VALIDATION ---
    let errors = {};

    // Core required fields (always required) - updated messages to lowercase
    if (!data.menuItemCode?.trim())
      errors.menuItemCode = "item code is required.";
    if (!data.menuItemName?.trim())
      errors.menuItemName = "item name is required.";
    // Food type is only required for menu items, not catalog items
    if (!isCatalog && !data.foodType) errors.foodType = "food type is required.";
    
    // Conditional validation based on isCatalog
    if (!isCatalog) {
      if (!data.prepTime) errors.prepTime = "prep time is required.";
      if (!data.waitTime) errors.waitTime = "wait time is required.";
      if (
        !data.dietTypes ||
        Object.values(data.dietTypes).filter(Boolean).length < 1
      ) {
        errors.dietTypes = "at least 1 diet type is required.";
      }
    }

    // Conditional validations based on variant selection
    if (data.hasVariant) {
      
      // With variant: validate images and variants for both catalog and menu
      if (!data.mediaFiles || data.mediaFiles.length < 1) {
        errors.mediaFiles = "at least 1 image is required.";
      } else if (data.mediaFiles.length > 4) {
        errors.mediaFiles = "maximum 4 images allowed.";
      }

      // With Variant: at least 1 variant with data - enhanced validation
      if (!data.variantRows || data.variantRows.length < 1) {
        errors.variantRows = "at least 1 variant is required.";
      } else {
        for (const v of data.variantRows) {
          
          // Check if v is a valid object
          if (!v || typeof v !== 'object') {
            console.error('Invalid variant object:', v);
            errors.variantRows = "each variant must have a type.";
            break;
          }
          
          // Check if variant property exists and has a value
          if (!v.variant || typeof v.variant !== 'string' || v.variant.trim() === '') {
            console.error('Variant missing or invalid type:', v.variant);
            errors.variantRows = "each variant must have a type.";
            break;
          }
          
          // Check price - only required for menu items, not catalog items
          if (!isCatalog) {
            // Check price - for variants with location-based pricing, check locationStock
            if (v.locationStock && Array.isArray(v.locationStock) && v.locationStock.length > 0) {
              // Check if at least one location has a price set
              const hasPrice = v.locationStock.some(loc => loc.price !== undefined && loc.price !== null && (Number(loc.price) > 0 || loc.price === 0));
              if (!hasPrice) {
                console.error('Variant missing price in locationStock:', v.variant);
                errors.variantRows = `variant '${v.variant}' requires a price for at least one location.`;
                break;
              }
            } else if (!v.price && v.price !== 0) {
              // Fallback: check direct price field if locationStock is not used
              console.error('Variant missing price:', v.price);
              errors.variantRows = `variant '${v.variant}' requires a price.`;
              break;
            }
          }
        }
      }

      // Additional validations only for menu (not catalog) - updated messages to lowercase
      if (!isCatalog) {
        if (!data.ingredients || data.ingredients.length < 1)
          errors.ingredients = "at least 1 ingredient is required.";

        // Allow Free Substitutions (only if variant)
        if (data.allowFreeSubstitutions) {
          // Only validate that substitutions array exists, don't require every ingredient to have substitutions
          if (!data.substitutions) {
            data.substitutions = [];
          }
        }

        // Allow Addition of Sides (only if variant)
        if (data.allowAdditionOfSides && (!data.sides || data.sides.length < 1)) {
          errors.sides = "at least 1 included menu item is required.";
        }

        // Allow Ingredient Removals (only if variant)
        if (
          data.allowIngredientRemovals &&
          (!data.removableIngredients || data.removableIngredients.length < 1)
        ) {
          errors.removableIngredients =
            "at least 1 removable ingredient is required.";
        }
      }
    } else if (!isCatalog) {
      // No variant: no validation for images, ingredients, allergens, or location fields
      // All these fields are optional when no variant is selected
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("please fix the errors in the form.");
      return;
    }

    // Ensure all locationValues have currency set (safeguard)
    if (data.locationValues) {
      const updatedLocationValues = { ...data.locationValues };
      let needsUpdate = false;
      
      Object.keys(updatedLocationValues).forEach((locValue) => {
        if (!updatedLocationValues[locValue]) {
          updatedLocationValues[locValue] = {};
        }
        if (!updatedLocationValues[locValue].currency) {
          updatedLocationValues[locValue].currency = "USD";
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        setValue("locationValues", updatedLocationValues);
        // Use updated values
        data.locationValues = updatedLocationValues;
      }
    }

    // Additional check: ensure variantRows are properly structured if hasVariant is true
    if (data.hasVariant && data.variantRows) {
      const hasInvalidVariants = data.variantRows.some(v => !v.variant);
      if (hasInvalidVariants) {
        console.error('Invalid variants detected:', data.variantRows);
        toast.error("please ensure all variants have proper names.");
        return;
      }
    }

    try {
      dispatch(setShowLoader(true));

      // Process media files
      let finalMediaFiles = [];
      if (mediaFiles?.length > 0) {
        finalMediaFiles = await processMediaFiles(mediaFiles, editItem);
      }

      // Transform data efficiently based on isCatalog
      const transformedIngredients = !isCatalog
        ? data.ingredients?.map((ingredientName) => {
            const substitution = data.substitutions?.find(
              (sub) => sub.ingredient === ingredientName
            );
            return {
              name: ingredientName,
              substitutes: substitution
                ? substitution.subs.filter((sub) => sub?.trim())
                : [],
            };
          }) || []
        : [];

      const transformedDietTypes = !isCatalog
        ? Object.entries(data.dietTypes || {})
            .filter(([, value]) => Boolean(value))
            .map(([key]) => key)
        : [];

      const transformedVariants = data.hasVariant
        ? (data.variantRows || []).map((variant) => {
            // Transform locationStock to include price and currency per location
            const perLocationStock = (variant.locationStock || []).map((loc) => ({
              locationId: loc.locationId,
              currency: loc.currency || "USD",
              price: Number(loc.price) || 0,
              sku: loc.sku || "",
              stockOnHand: Number(loc.stockOnHand) || 0,
              stockCommitted: Number(loc.stockCommitted) || 0,
              awaitingNewStockDelivery: loc.awaitingNewStockDelivery || "",
              awaitingNewStockCount: Number(loc.awaitingNewStockCount) || 0,
            }));

            // Generate code: baseItemCode + "-" + variantNumber
            const baseCode = data.menuItemCode || "";
            const variantCode = baseCode && variant.variantNumber 
              ? `${baseCode}-${variant.variantNumber}` 
              : "";
            
            return {
              name: variant.variant,
              description: variant.description || "",
              code: variantCode,
              // For catalog items, stock information is now location-based
              ...(isCatalog && {
                stockCount: perLocationStock.reduce(
                  (total, loc) => total + (Number(loc.stockOnHand) || 0),
                  0
                ),
                perLocationStock: perLocationStock,
              }),
              // For menu items, include nutritional and location stock info
              ...(!isCatalog && {
                nutrition: {
                  weight: Number(variant.weight) || 0,
                  kcal: Number(variant.kcal) || 0,
                  protein: Number(variant.protein) || 0,
                  carbohydrates: Number(variant.carbohydrates) || 0,
                  fats: Number(variant.fats) || 0,
                  fibre: Number(variant.fibre) || 0,
                },
                sku: variant.sku || "",
                barcode: variant.barcode || "",
                qrCodeImage: variant.qrCodeImage || "",
                barcodeImage: variant.barcodeImage || "",
                perLocationStock: perLocationStock,
                totalStock: {
                  onHand: Number(variant.stockOnHand) || 0,
                  committed: Number(variant.stockCommitted) || 0,
                  awaitingDeliveryDate:
                    variant.awaitingNewStockDelivery || undefined,
                  awaitingQty: Number(variant.awaitingNewStockCount) || 0,
                },
                showStockToCustomer: variant.showStockToCustomer || false,
                allowSaleIfOutOfStock: variant.allowSaleIfOutOfStock || false,
              }),
            };
          })
        : [];

      // Handle location data for no variant case (for both menu and catalog)
      let locationStock = [];
      if (!data.hasVariant && data.locations?.length > 0) {
        // Filter locationValues to only include selected locations
        const selectedLocationValues = {};
        if (data.locationValues && data.locations) {
          data.locations.forEach((locValue) => {
            if (data.locationValues[locValue]) {
              selectedLocationValues[locValue] = data.locationValues[locValue];
            }
          });
        }
        
        locationStock = transformLocationValuesWithId(
          selectedLocationValues,
          locationsListOptions
        );
      }

      // Fix the payload transformation to match exactly what backend expects
      const transformedPayload = {
        code: data.menuItemCode,
        name: data.menuItemName,
        description: data.itemDescription || "",
        imageVideo: finalMediaFiles.length > 0 ? finalMediaFiles : undefined,
        isCatalog: isCatalog,
        ...(!isCatalog && {
          foodType: data.foodType,
          prepTime: Number(data.prepTime),
          waitTime: Number(data.waitTime),
          dietTypes: transformedDietTypes,
          ingredients: transformedIngredients,
          allowFreeSubstitution: data.allowFreeSubstitutions || false,
          allowIngredientRemoval: data.allowIngredientRemovals || false,
          removableIngredients: data.removableIngredients || [],
          // Only include sides if allowAdditionOfSides is true
          ...(data.allowAdditionOfSides && {
            sides: data.sides || [],
            allowSideAddition: true,
          }),
          // When allowAdditionOfSides is false, don't include sides field at all
          allergens: data.allergens || [],
          variantEnabled: data.hasVariant || false,
          ...(data.hasVariant &&
            transformedVariants.length > 0 && { variants: transformedVariants }),
          ...(!data.hasVariant && locationStock.length > 0 && { locationStock }),
          showStockToCustomer: data.showStockToCustomer || false,
          allowSaleIfOutOfStock: data.allowSaleIfOutOfStock || false,
        }),
        // For catalog, include variants and basic fields
        ...(isCatalog && {
          variantEnabled: data.hasVariant || false,
          ...(data.hasVariant &&
            transformedVariants.length > 0 && { variants: transformedVariants }),
          ...(!data.hasVariant && locationStock.length > 0 && { locationStock }),
        }),
      };

      if (editItem && editItem._id) {
        // Edit mode
        editMenuItem(
          {
            businessId: selectedBusinessId,
            menuItemId: editItem._id,
            data: transformedPayload,
          },
          {
            onSuccess: (data) => {
              toast.success(data?.message || "menu item updated  !");
              queryClient.invalidateQueries(["menuItems", selectedBusinessId, isCatalog], {
                exact: true,
              });
              queryClient.refetchQueries(["menuItems", selectedBusinessId, isCatalog], {
                exact: true,
              });
              dispatch(setShowLoader(false));
              // Reset form to default values before closing
              const defaultValues = getDefaultValues();
              reset(defaultValues, { keepDefaultValues: false });
              // Explicitly clear foodType to ensure Autocomplete resets
              setValue("foodType", "");
              setFormErrors({});
              // Update form key to force remount of Autocomplete on next open
              setFormKey(Date.now());
              handleClose();
            },
            onError: (error) => {
              toast.error(
                error?.response?.data?.message ||
                  "failed to update menu item. please try again."
              );
              dispatch(setShowLoader(false));
            },
          }
        );
      } else {
        // Create mode
        submitMenuItem(
          { businessId: selectedBusinessId, data: transformedPayload },
          {
            onSuccess: (data) => {
              toast.success(data?.message || "menu item added  !");
              queryClient.invalidateQueries(["menuItems", selectedBusinessId, isCatalog], {
                exact: true,
              });
              queryClient.refetchQueries(["menuItems", selectedBusinessId, isCatalog], {
                exact: true,
              });
              dispatch(setShowLoader(false));
              // Reset form to default values before closing
              const defaultValues = getDefaultValues();
              reset(defaultValues, { keepDefaultValues: false });
              // Explicitly clear foodType to ensure Autocomplete resets
              setValue("foodType", "");
              setFormErrors({});
              // Update form key to force remount of Autocomplete on next open
              setFormKey(Date.now());
              handleClose();
            },
            onError: (error) => {
              toast.error(
                error?.response?.data?.message ||
                  "failed to add menu item. please try again."
              );
              dispatch(setShowLoader(false));
            },
          }
        );
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("failed to add menu item");
      dispatch(setShowLoader(false));
    }
  };

  // Process media files for both create and edit scenarios
  const processMediaFiles = async (currentMediaFiles, editItemData) => {
    if (!editItemData) {
      // Create mode: upload all files
      return await uploadMediaFiles(currentMediaFiles);
    }

    // Edit mode: upload new files, keep existing ones as-is
    const finalFileNames = [];
    for (const mediaFile of currentMediaFiles) {
      if (mediaFile.file instanceof File) {
        try {
          const fileKey = await uploadSingleFile(mediaFile.file);
          if (fileKey) finalFileNames.push(fileKey);
        } catch (error) {
          console.error("Failed to upload file:", mediaFile.file.name, error);
          toast.error(`Failed to upload ${mediaFile.file.name}`);
        }
      } else if (typeof mediaFile.file === "string") {
        // Existing file: keep as-is
        finalFileNames.push(mediaFile.file);
      }
    }
    return finalFileNames;
  };

  // Transform API currency data to match expected format
  const currencies = useMemo(() => {
    if (!currencyData?.currencies) {
      // Fallback to default currencies if API data is not available
      return [
        { code: "USD", label: "united states (usd) $", symbol: "$" },
        { code: "EUR", label: "euro (eur) €", symbol: "€" },
        { code: "GBP", label: "british pound (gbp) £", symbol: "£" },
        { code: "JPY", label: "japanese yen (jpy) ¥", symbol: "¥" },
        { code: "CAD", label: "canadian dollar (cad) $", symbol: "$" },
        { code: "CNY", label: "chinese yuan (cny) ¥", symbol: "¥" },
        { code: "AUD", label: "australian dollar (aud) $", symbol: "$" },
        { code: "CHF", label: "swiss franc (chf) fr", symbol: "Fr" },
        { code: "HKD", label: "hong kong dollar (hkd) $", symbol: "$" },
        { code: "INR", label: "indian rupee (inr) ₹", symbol: "₹" },
      ];
    }

    // Get unique currencies (group by currency code)
    const uniqueCurrencies = new Map();
    currencyData.currencies.forEach((item) => {
      const currencyCode = item.currency;
      if (!uniqueCurrencies.has(currencyCode)) {
        const symbol = item.symbol || currencyCode;
        uniqueCurrencies.set(currencyCode, {
          code: currencyCode,
          label: `${item.name.toLowerCase()} (${currencyCode.toLowerCase()}) ${symbol}`,
          symbol: symbol,
          iso2: item.iso2, // Store iso2 for country matching
        });
      }
    });

    // Convert map to array and sort by currency code
    return Array.from(uniqueCurrencies.values()).sort((a, b) =>
      a.code.localeCompare(b.code)
    );
  }, [currencyData]);


  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: { xs: 1, sm: 3 }, width: "100%" }}>
      <Box sx={{ maxHeight: "80vh", overflowY: "auto", pr: 2 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card sx={{ mb: 3, borderRadius: { xs: 0, sm: 2 } }}>
            <CardContent>
              {/* Header */}
              <Box
                sx={{
                  width: "100%",
                  color: "#8000c2",
                  px: 0,
                  pt: 0,
                  pb: 0,
                  position: "relative",
                  minHeight: 54,
                  borderTopLeftRadius: { xs: "24px", sm: 12 },
                  borderTopRightRadius: { xs: "24px", sm: 12 },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: 8,
                    borderTopLeftRadius: { xs: "24px", sm: 12 },
                    borderTopRightRadius: { xs: "24px", sm: 12 },
                  }}
                />
                <Typography
                  sx={{
                    pl: 3,
                    pt: 1.3,
                    fontWeight: 700,
                    fontSize: 21,
                    color: "#000",
                    letterSpacing: 0.2,
                    position: "relative",
                    zIndex: 1,
                    width: "fit-content",
                    borderTopLeftRadius: 7,
                  }}
                                  >
                    {isCatalog 
                      ? (editItem ? "edit catalog item" : "create catalog item")
                      : (editItem ? "edit menu item" : "create menu item")
                    }
                  </Typography>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 13,
                    color: "#c09adf",
                    background: "transparent",
                    "&:hover": { color: "#8000c2", background: "#f3e7ff" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 27 }} />
                </IconButton>
              </Box>

              <Controller
                name="mediaFiles"
                control={control}
                render={({ field }) => (
                  <MediaUploader
                    mediaFiles={field.value || []}
                    setMediaFiles={(files) => {
                      field.onChange(files);
                      clearFieldError("mediaFiles");
                    }}
                  />
                )}
              />
              {formErrors.mediaFiles && (
                <Typography color="error">{formErrors.mediaFiles}</Typography>
              )}

              <Controller
                name="menuItemCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder={isCatalog ? "enter catalog item code" : "enter menu item code"}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    onChange={(e) => {
                      field.onChange(e);
                      clearFieldError("menuItemCode");
                    }}
                  />
                )}
              />
              {formErrors.menuItemCode && (
                <Typography color="error">{formErrors.menuItemCode}</Typography>
              )}

              <Controller
                name="menuItemName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder= {isCatalog ? "enter catalog item name" : "enter menu item name"}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    onChange={(e) => {
                      field.onChange(e);
                      clearFieldError("menuItemName");
                    }}
                  />
                )}
              />
              {formErrors.menuItemName && (
                <Typography color="error">{formErrors.menuItemName}</Typography>
              )}

              {/* Food Type Selection - Only show for menu items, not catalog */}
              {!isCatalog && (
                <>
                  <Controller
                    name="foodType"
                    control={control}
                    render={({ field }) => {
                      // Find the matching option in foodTypeOptions
                      let selectedOption = foodTypeOptions.find(
                        (option) => option.id === field.value
                      );
                      
                      // If no match found but we have editItem with foodType, create a temporary option
                      let optionsToUse = foodTypeOptions;
                      if (!selectedOption && field.value && editItem?.foodType) {
                        selectedOption = {
                          id: editItem.foodType._id,
                          label: editItem.foodType.name,
                          value: editItem.foodType.name,
                        };
                        // Add it to options temporarily for display
                        optionsToUse = [...foodTypeOptions];
                        if (!optionsToUse.find(opt => opt.id === selectedOption.id)) {
                          optionsToUse.push(selectedOption);
                        }
                      }
                      
                      return (
                        <Autocomplete
                          key={editItem ? `edit-${editItem._id}` : `new-${formKey}`}
                          options={optionsToUse}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option.label || option.name || '';
                          }}
                          value={selectedOption || null}
                          onChange={(event, newValue) => {
                            field.onChange(newValue ? newValue.id : "");
                            clearFieldError("foodType");
                          }}
                          freeSolo
                          isOptionEqualToValue={(option, value) => {
                            // Compare by ID to ensure proper matching
                            return option.id === value?.id;
                          }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="select food type"
                            placeholder="search food types..."
                            size="small"
                            sx={{ mb: 2 }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  {params.inputProps.value &&
                                    !foodTypeOptions.some((option) =>
                                      option.label
                                        .toLowerCase()
                                        .includes(
                                          params.inputProps.value.toLowerCase()
                                        )
                                    ) && (
                                      <InputAdornment position="end">
                                        <Button
                                          size="small"
                                          variant="contained"
                                          startIcon={
                                            addFoodTypeMutation.isLoading ? (
                                              <CircularProgress size={16} />
                                            ) : (
                                              <Add />
                                            )
                                          }
                                          onClick={() =>
                                            handleAddNew(
                                              params.inputProps.value,
                                              addFoodTypeMutation,
                                              "foodType"
                                            )
                                          }
                                          disabled={addFoodTypeMutation.isLoading}
                                          sx={{
                                            minWidth: "auto",
                                            px: 1,
                                            py: 0.5,
                                            fontSize: "0.75rem",
                                            textTransform: "none",
                                          }}
                                        >
                                          add
                                        </Button>
                                      </InputAdornment>
                                    )}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    );
                  }}
                  />
                  {formErrors.foodType && (
                    <Typography color="error">{formErrors.foodType}</Typography>
                  )}
                </>
              )}

              <Controller
                name="itemDescription"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="item description"
                    fullWidth
                    multiline
                    rows={4}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {/* Conditional rendering based on isCatalog */}
              {!isCatalog && (
                <>
                  {/* Prep and Wait Time */}
                  <Grid
                    container
                    spacing={2}
                    sx={{ mb: 2 }}
                    direction={isXs ? "column" : "row"}
                  >
                    <Grid
                      item
                      xs={12}
                      sm={IsMaxView ? 6 : 12}
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          prep time
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", mb: 1, display: "block" }}
                        >
                          time taken to prepare the food
                        </Typography>
                      </Box>
                      <FormControl fullWidth size="small">
                        <Controller
                          name="prepTime"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                clearFieldError("prepTime");
                              }}
                            >
                              <MenuItem value={5}>5min</MenuItem>
                              <MenuItem value={10}>10min</MenuItem>
                              <MenuItem value={15}>15min</MenuItem>
                              <MenuItem value={30}>30min</MenuItem>
                            </Select>
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={IsMaxView ? 6 : 12}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        wait time
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", mb: 1, display: "block" }}
                      >
                        time taken to serve the food; this can change depending on
                        the rush
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Controller
                          name="waitTime"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                clearFieldError("waitTime");
                              }}
                            >
                              <MenuItem value={5}>5min</MenuItem>
                              <MenuItem value={10}>10min</MenuItem>
                              <MenuItem value={15}>15min</MenuItem>
                              <MenuItem value={30}>30min</MenuItem>
                            </Select>
                          )}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  {formErrors.prepTime && (
                    <Typography color="error">{formErrors.prepTime}</Typography>
                  )}
                  {formErrors.waitTime && (
                    <Typography color="error">{formErrors.waitTime}</Typography>
                  )}

                  {/* Diet Types */}
                  <Typography variant="body2" sx={{ fontWeight: "bold", mb: 2 }}>
                    diet type
                  </Typography>
                  <Grid container spacing={1}>
                    {dietType?.dietTypes?.map((option) => (
                      <Grid item xs={6} sm={IsMaxView ? 4 : 6} key={option._id}>
                        <FormControlLabel
                          control={
                            <Controller
                              name={`dietTypes.${option._id}`}
                              control={control}
                              render={({ field }) => (
                                <Checkbox
                                  {...field}
                                  checked={Boolean(field.value)}
                                  onChange={(e) => {
                                    field.onChange(e.target.checked);
                                    clearFieldError("dietTypes");
                                  }}
                                />
                              )}
                            />
                          }
                          label={
                            <Box
                              sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                              <span>{option.name}</span>
                              {option.icon && (
                                <Box
                                  component="img"
                                  src={option.icon}
                                  alt={option.name}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    objectFit: "contain",
                                  }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {formErrors.dietTypes && (
                    <Typography color="error">{formErrors.dietTypes}</Typography>
                  )}
                </>
              )}
            </CardContent>

            <CardContent>
              {/* Ingredients - Only show for menu, not catalog */}
              {!isCatalog && (
                <>
                  <Typography  pb={4}  sx={{ fontWeight: "bold", fontSize: 14 }}>
                    ingredients
                  </Typography>
              <Controller
                name="ingredients"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={ingredientOptions}
                    getOptionLabel={(option) => option.label}
                    value={ingredientOptions.filter((option) =>
                      (field.value || []).includes(option.value)
                    )}
                    onChange={(event, newValue) =>
                      field.onChange(newValue.map((item) => item.value))
                    }
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="select ingredients"
                        placeholder="Search ingredients..."
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              {params.inputProps.value &&
                                !ingredientOptions.some((option) =>
                                  option.label
                                    .toLowerCase()
                                    .includes(
                                      params.inputProps.value.toLowerCase()
                                    )
                                ) && (
                                  <InputAdornment position="end">
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={
                                        addIngredientMutation.isLoading ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <Add />
                                        )
                                      }
                                      onClick={() =>
                                        handleAddNew(
                                          params.inputProps.value,
                                          addIngredientMutation,
                                          "ingredients",
                                          true
                                        )
                                      }
                                      disabled={addIngredientMutation.isLoading}
                                      sx={{
                                        minWidth: "auto",
                                        px: 1,
                                        py: 0.5,
                                        fontSize: "0.75rem",
                                        textTransform: "none",
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </InputAdornment>
                                )}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          {...getTagProps({ index })}
                          key={option.value}
                        />
                      ))
                    }
                    sx={{ mb: 2 }}
                  />
                )}
              />
              {formErrors.ingredients && (
                <Typography color="error">{formErrors.ingredients}</Typography>
              )}

              {/* Allow Free Substitutions */}
              {selectedIngredients?.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mr: 1 }}
                  >
                    allow free substitutions
                  </Typography>
                  <Info sx={{ fontSize: 16, color: "grey.500", mr: 1 }} />
                  <Controller
                    name="allowFreeSubstitutions"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          clearFieldError("allowFreeSubstitutions");
                          clearFieldError("substitutions");
                        }}
                      />
                    )}
                  />
                </Box>
              )}
              {formErrors.allowFreeSubstitutions && (
                <Typography color="error">
                  {formErrors.allowFreeSubstitutions}
                </Typography>
              )}

              {/* Substitutions Table */}
              {selectedIngredients?.length > 0 && allowFreeSubstitutions && (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mb: 2 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ textTransform: "lowercase" }}>
                        <TableCell>Ingredient</TableCell>
                        <TableCell>sub 1</TableCell>
                        <TableCell>sub 2</TableCell>
                        <TableCell>sub 3</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedIngredients.map((ingredient, index) => (
                        <TableRow key={index}>
                          <TableCell>{ingredient}</TableCell>
                          {[0, 1, 2].map((subIndex) => (
                            <TableCell key={subIndex}>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={getSubstitutionValue(
                                    ingredient,
                                    subIndex
                                  )}
                                  onChange={(e) =>
                                    handleSubstitutionChange(
                                      ingredient,
                                      subIndex,
                                      e.target.value
                                    )
                                  }
                                >
                                  {ingredientOptions
                                    .filter((ing) => ing.value !== ingredient)
                                    .map((ing) => (
                                      <MenuItem
                                        key={ing.value}
                                        value={ing.value}
                                      >
                                        {ing.value}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {formErrors.substitutions && (
                <Typography color="error">
                  {formErrors.substitutions}
                </Typography>
              )}

              {/* Allow Addition of Sides - Updated to use menu items */}
              <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mr: 1 }}>
                  allow addition of sides
                </Typography>
                <Info sx={{ fontSize: 16, color: "grey.500", mr: 1 }} />
                <Controller
                  name="allowAdditionOfSides"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        clearFieldError("allowAdditionOfSides");
                        clearFieldError("sides");  // Changed back to 'sides'
                      }}
                    />
                  )}
                />
              </Box>
              {formErrors.allowAdditionOfSides && (
                <Typography color="error">
                  {formErrors.allowAdditionOfSides}
                </Typography>
              )}

              {/* Included Menu Items Selection - using 'sides' field */}
              {allowAdditionOfSides && (
                <>
                  <Controller
                    name="sides"  // Changed back to 'sides'
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={menuItemOptions}
                        getOptionLabel={(option) => option.label}
                        value={menuItemOptions.filter((option) =>
                          (field.value || []).includes(option.id)
                        )}
                        onChange={(event, newValue) =>
                          field.onChange(newValue.map((item) => item.id))
                        }
                        filterSelectedOptions
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="select included menu items"
                            placeholder="search menu items..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={`${option.label} (${option.currency} ${option.price})`}
                              {...getTagProps({ index })}
                              key={option.id}
                            />
                          ))
                        }
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                  <Box sx={{ mb: 3 }}>
                    {watch("sides")?.map((menuItemId) => {  // Changed back to 'sides'
                      const menuItem = menuItemOptions.find(item => item.id === menuItemId);
                      return (
                        <Chip
                          key={menuItemId}
                          label={menuItem ? `${menuItem.label} (${menuItem.currency} ${menuItem.price})` : menuItemId}
                          onDelete={() => {
                            const currentItems = watch("sides") || [];  // Changed back to 'sides'
                            setValue(
                              "sides",  // Changed back to 'sides'
                              currentItems.filter((item) => item !== menuItemId)
                            );
                          }}
                          sx={{ mr: 1, mb: 1 }}
                        />
                      );
                    })}
                  </Box>
                </>
              )}
              {formErrors.sides && (  // Changed back to 'sides'
                <Typography color="error">{formErrors.sides}</Typography>
              )}

              {/* Allow Ingredient Removals */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mr: 1 }}>
                  allow ingredient removals
                </Typography>
                <Info sx={{ fontSize: 16, color: "grey.500", mr: 1 }} />
                <Controller
                  name="allowIngredientRemovals"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        clearFieldError("allowIngredientRemovals");
                        clearFieldError("removableIngredients");
                      }}
                    />
                  )}
                />
              </Box>
              {formErrors.allowIngredientRemovals && (
                <Typography color="error">
                  {formErrors.allowIngredientRemovals}
                </Typography>
              )}

              {/* Removable Ingredients */}
              {allowIngredientRemovals && (
                <Controller
                  name="removableIngredients"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={ingredientOptions}
                      getOptionLabel={(option) => option.label}
                      value={ingredientOptions.filter((option) =>
                        (field.value || []).includes(option.value)
                      )}
                      onChange={(event, newValue) =>
                        field.onChange(newValue.map((item) => item.value))
                      }
                      filterSelectedOptions
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="select ingredients"
                          placeholder="Search ingredients..."
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {params.InputProps.endAdornment}
                                {params.inputProps.value &&
                                  !ingredientOptions.some((option) =>
                                    option.label
                                      .toLowerCase()
                                      .includes(
                                        params.inputProps.value.toLowerCase()
                                      )
                                  ) && (
                                    <InputAdornment position="end">
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={
                                          addIngredientMutation.isLoading ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <Add />
                                          )
                                        }
                                        onClick={() =>
                                          handleAddNew(
                                            params.inputProps.value,
                                            addIngredientMutation,
                                            "removableIngredients",
                                            true
                                          )
                                        }
                                        disabled={
                                          addIngredientMutation.isLoading
                                        }
                                        sx={{
                                          minWidth: "auto",
                                          px: 1,
                                          py: 0.5,
                                          fontSize: "0.75rem",
                                          textTransform: "none",
                                        }}
                                      >
                                        Add
                                      </Button>
                                    </InputAdornment>
                                  )}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option.label}
                            {...getTagProps({ index })}
                            key={option.value}
                          />
                        ))
                      }
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              )}
              {formErrors.removableIngredients && (
                <Typography color="error">
                  {formErrors.removableIngredients}
                </Typography>
              )}
                </>
              )}

              {/* Allergens - Only show for menu, not catalog */}
              {!isCatalog && (
                <>
                  <Typography pb={4}  sx={{ fontWeight: "bold", fontSize: 14 }}>
                    allergens
                  </Typography>
              <Controller
                name="allergens"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={allergenOptions}
                    getOptionLabel={(option) => option.label}
                    value={allergenOptions.filter((option) =>
                      (field.value || []).includes(option.value)
                    )}
                    onChange={(event, newValue) =>
                      field.onChange(newValue.map((item) => item.value))
                    }
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="select allergens"
                        placeholder="Search allergens..."
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              {params.inputProps.value &&
                                !allergenOptions.some((option) =>
                                  option.label
                                    .toLowerCase()
                                    .includes(
                                      params.inputProps.value.toLowerCase()
                                    )
                                ) && (
                                  <InputAdornment position="end">
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={
                                        addAllergenMutation.isLoading ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <Add />
                                        )
                                      }
                                      onClick={() =>
                                        handleAddNew(
                                          params.inputProps.value,
                                          addAllergenMutation,
                                          "allergens",
                                          true
                                        )
                                      }
                                      disabled={addAllergenMutation.isLoading}
                                      sx={{
                                        minWidth: "auto",
                                        px: 1,
                                        py: 0.5,
                                        fontSize: "0.75rem",
                                        textTransform: "none",
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </InputAdornment>
                                )}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          {...getTagProps({ index })}
                          key={option.value}
                        />
                      ))
                    }
                    sx={{ mb: 2 }}
                  />
                )}
              />

              <Box sx={{ mb: 3 }}>
                {watch("allergens")?.length > 0 && (
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      ml: 1,
                      bgcolor: "transparent",
                      textDecoration: "underline",
                      textTransform: "none",
                      "&:hover": { bgcolor: "transparent" },
                    }}
                  >
                    see all
                  </Button>
                )}
              </Box>
                </>
              )}

              {/* Variants - Show for both catalog and menu */}
              <Controller
                name="hasVariant"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    row
                    value={field.value === true || field.value === "true"}
                    onChange={(e) => field.onChange(e.target.value === "true")}
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label="variant"
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="no variant"
                    />
                  </RadioGroup>
                )}
              />
              {formErrors.hasVariant && (
                <Typography color="error">{formErrors.hasVariant}</Typography>
              )}

              {/* Variant Details - Show for both catalog and menu */}
              {hasVariant ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mt: 2, mb: 2 }}
                  >
                    variants
                  </Typography>
                  <FormControl size="small" sx={{ mb: 2, minWidth: 120 }}>
                    <InputLabel id="multi-select-label">
                      Select Options
                    </InputLabel>
                    <Select
                      labelId="multi-select-label"
                      multiple
                      value={variantRows?.map((row) => row.variant) || []}
                      onChange={(e) =>
                        handleVariantSelectionChange(e.target.value)
                      }
                      label="Select Options"
                      renderValue={(selected) => selected.join(", ")}
                    >
                      {options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{textTransform: "lowercase"}}>
                          <TableCell>variant</TableCell>
                          <TableCell>stock on hand</TableCell>
                          <TableCell>price</TableCell>
                          <TableCell>details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(variantRows || []).map((row, index) => {
                          const locationStock = row.locationStock || [];
                          const baseCode = watch("menuItemCode") || "";
                          // Generate code: baseCode + "-" + variantNumber
                          const code = baseCode && row.variantNumber 
                            ? `${baseCode}-${row.variantNumber}` 
                            : "";
                          const variantTypeLabel = options.find((opt) => opt.value === row.variant)?.label || row.variant;
                          const variantLabel = code 
                            ? `${code} - ${variantTypeLabel}`
                            : variantTypeLabel;
                          
                          // If no locations, show single row
                          if (locationStock.length === 0) {
                            return (
                              <TableRow key={row.variant}>
                                <TableCell>{variantLabel}</TableCell>
                                <TableCell>
                                  {isCatalog ? (row.stockCount || 0) : (row.stockOnHand || 0)}
                                </TableCell>
                                <TableCell>no price set</TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDetails(row.variant)}
                                  >
                                    <MoreHoriz />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          }
                          
                          // Show one row per location
                          return locationStock.map((loc, locIndex) => {
                            const locationObj = locationsListOptions.find(l => l.id === loc.locationId);
                            const locationName = locationObj?.label || `location ${locIndex + 1}`;
                            const currencySymbol = currencies.find((c) => c.code === (loc.currency || "USD"))?.symbol || "$";
                            
                            return (
                              <TableRow key={`${row.variant}-${loc.locationId}-${locIndex}`}>
                                <TableCell>
                                  {locIndex === 0 ? variantLabel : ""}
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                                      {locationName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 'medium' }}>
                                      stock: {loc.stockOnHand || 0}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'medium' }}>
                                      {currencySymbol}{loc.price || 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                      {loc.currency || "USD"}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {locIndex === 0 && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDetails(row.variant)}
                                    >
                                      <MoreHoriz />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          });
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <VariantDialog
                    open={dialogOpen || false}
                    onClose={() => setValue("dialogOpen", false)}
                    onSave={handleSaveVariantDetails}
                    defaultValues={
                      currentVariantValue !== null
                        ? transformVariantDataForDialog(
                            (variantRows || []).find(
                              (row) => row.variant === currentVariantValue
                            )
                          )
                        : {}
                    }
                    locationsListOptions={locationsListOptions}
                    isCatalog={isCatalog}
                    businessLocation={businessLocation}
                    baseItemCode={watch("menuItemCode") || ""}
                  />
                </>
              ) : (
                /* Stock Information - Show for both menu and catalog */
                <CardContent sx={{ p: 0 , pb: "0px !important"}}>
                  <Typography
                    sx={{ mt: 3, mb: 2, fontWeight: "bold", fontSize: 14 }}
                  >
                    stock information
                  </Typography>

                  <Typography
                    sx={{ mt: 3, mb: 2, fontWeight: "bold", fontSize: 14 }}
                  >
                    per location stock count
                  </Typography>

                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>select locations</InputLabel>
                    <Controller
                      name="locations"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          multiple
                          label="select locations"
                          value={field.value || []}
                          onChange={(e) => {
                            const selectedValues = e.target.value;
                            const previousValues = field.value || [];
                            
                            // Remove deselected locations from locationValues
                            const currentLocationValues = watch("locationValues") || {};
                            const updatedLocationValues = { ...currentLocationValues };
                            
                            // Find locations that were deselected
                            const deselected = previousValues.filter(
                              (loc) => !selectedValues.includes(loc)
                            );
                            
                            // Remove deselected locations from locationValues
                            deselected.forEach((locValue) => {
                              delete updatedLocationValues[locValue];
                            });
                            
                            // Update locationValues to remove deselected locations
                            setValue("locationValues", updatedLocationValues);
                            
                            field.onChange(selectedValues);
                            // Set default currency for newly selected locations (synchronous now)
                            setDefaultCurrencyForLocations(selectedValues);
                          }}
                          renderValue={(selected) =>
                            locationsListOptions
                              .filter((opt) => selected.includes(opt.value))
                              .map((opt) => opt.label)
                              .join(", ")
                          }
                        >
                          {locationsListOptions.map((location) => (
                            <MenuItem key={location.id} value={location.value}>
                              <Checkbox
                                checked={(field.value || []).includes(
                                  location.value
                                )}
                              />
                              <ListItemText primary={location.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                  {formErrors.locations && (
                    <Typography color="error">
                      {formErrors.locations}
                    </Typography>
                  )}
                  {/* Render dynamic fields for each selected location */}
                  {renderLocationFields()}
                </CardContent>
              )}
            </CardContent>
            {/* Stock switches - Only show for menu, not catalog */}
            {!isCatalog && (
              <CardContent>
                <Box sx={{ mt: 3 }}>
                  <FormControlLabel
                    control={
                      <Controller
                        name="showStockToCustomer"
                        control={control}
                        render={({ field }) => (
                          <Switch {...field} checked={field.value} />
                        )}
                      />
                    }
                    label="show stock count to customer"
                  />
                  <FormControlLabel
                    control={
                      <Controller
                        name="allowSaleIfOutOfStock"
                        control={control}
                        render={({ field }) => (
                          <Switch {...field} checked={field.value} />
                        )}
                      />
                    }
                    label="allow sale if out of stock"
                  />
                </Box>
              </CardContent>
            )}

            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                variant="contained"
                type="submit"
                size="large"
                fullWidth={isXs}
                disabled={isSubmitting || isEditing}
                sx={{
                  textTransform: "lowercase",
                  mb: 8,
                }}
              >
                {isSubmitting || isEditing ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={20} color="inherit" />
                    Saving...
                  </Box>
                ) : (
                  `save ${isCatalog ? "catalog" : "menu"} item`
                )}
              </Button>
            </Box>
          </Card>
        </form>
      </Box>
    </Box>
  );
};

export default AddMenuItemForm;
